import { useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";

const ROLES = [
  ["developer", "Developer"],
  ["investor", "Investor"],
  ["broker", "Broker"],
  ["introducer", "Introducer"],
  ["funder", "Funder"],
  ["family office", "Family office"],
  ["other", "Other"],
];

export default function Brief() {
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const refs = { email: useRef(null), consent: useRef(null) };

  function validate(form) {
    const next = {};
    const email = form.email.value.trim();
    if (!email) next.email = "Enter your work email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      next.email = "Enter a valid email address, e.g. name@company.com.";
    if (!form.consent.checked) next.consent = "Tick the box to confirm you agree to receive the Brief.";
    return next;
  }

  async function onSubmit(e) {
    e.preventDefault();
    const form = e.target;
    setServerError("");
    const next = validate(form);
    setErrors(next);
    const firstBad = ["email", "consent"].find((k) => next[k]);
    if (firstBad) {
      refs[firstBad].current && refs[firstBad].current.focus();
      return;
    }
    if (form.website.value) { setDone(true); return; }

    const body = {
      email: form.email.value.trim(),
      name: form.name.value.trim(),
      company: form.company.value.trim(),
      role: form.role.value,
      consent: true,
      source: (params.get("src") || "web").slice(0, 64),
    };
    setBusy(true);
    try {
      const r = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) setDone(true);
      else setServerError(data.error || "We could not process your request. Please try again shortly.");
    } catch {
      setServerError("We could not process your request. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="page-header">
        <div className="wrap">
          <div className="eyebrow">Daksfirst Market Brief</div>
          <h1>Get the Daksfirst Market Brief by email.</h1>
          <p className="sub">
            A weekly read on UK property finance: rates and funding costs, the refinance
            market, rents and the yield gap, the Daksfirst Market Tracker and the week ahead.
            Monthly edition on the fifth working day.
          </p>
        </div>
      </section>

      <section>
        <div className="wrap brief-wrap">
          {done ? (
            <div role="status" aria-live="polite">
              <h2 style={{ color: "var(--white)", marginBottom: 14 }}>You are subscribed.</h2>
              <p className="sub">The next edition will reach you when it is published.</p>
            </div>
          ) : (
            <form className="contact" onSubmit={onSubmit} noValidate>
              <div>
                <label htmlFor="email">Work email</label>
                <input
                  id="email" name="email" type="email" ref={refs.email} autoComplete="email"
                  required aria-required="true"
                  aria-invalid={errors.email ? "true" : undefined}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <span id="email-error" className="field-error" role="alert">{errors.email}</span>
                )}
              </div>
              <div>
                <label htmlFor="name">Name (optional)</label>
                <input id="name" name="name" type="text" autoComplete="name" />
              </div>
              <div>
                <label htmlFor="company">Company (optional)</label>
                <input id="company" name="company" type="text" autoComplete="organization" />
              </div>
              <div>
                <label htmlFor="role">Role (optional)</label>
                <select id="role" name="role" defaultValue="">
                  <option value="">Select</option>
                  {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="hp" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
              </div>
              <div>
                <label className="check" htmlFor="consent">
                  <input
                    id="consent" name="consent" type="checkbox" ref={refs.consent}
                    required aria-required="true"
                    aria-invalid={errors.consent ? "true" : undefined}
                    aria-describedby={errors.consent ? "consent-error" : undefined}
                  />
                  <span>
                    I agree to receive the Daksfirst Market Brief by email. I can unsubscribe at
                    any time using the link in every email.
                  </span>
                </label>
                {errors.consent && (
                  <span id="consent-error" className="field-error" role="alert">{errors.consent}</span>
                )}
              </div>
              {serverError && (
                <p className="field-error" role="alert">{serverError}</p>
              )}
              <button className="btn btn-primary" type="submit" disabled={busy} style={{ alignSelf: "flex-start" }}>
                {busy ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
          )}
          <p className="small muted spacer-m">
            Market information only. It is not advice, an offer or a recommendation. See our{" "}
            <Link to="/privacy" style={{ color: "var(--gold)" }}>privacy policy</Link>.
          </p>
        </div>
      </section>
    </>
  );
}
